import { useEffect } from 'react';
import { Box, Flex, Grid } from 'components/box';
import { Field, Form } from 'react-final-form';
import { Text } from 'components/typography';
import { closeDrawer, openDrawer } from '../../redux/global/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useLogin } from 'hooks/customer';
import Button from 'components/button';
import Divider from './divider';
import LabeledInput from 'components/form/input';
import createDecorator from 'final-form-focus';
import isEmail from 'sane-email-validation';
import { ErrorBox } from './error-box';
import { RootState } from 'typesafe-actions';
import SectionTitle from 'components/_shared/elements/section-title';
import { css } from '@emotion/react';
import { useVodapayActive } from 'hooks/app';
import VodapayAuth from 'components/_shared/widgets/vodapay-authentication';
import { AuthenticationContext } from 'types/types';
import SocialAuth from './social-auth';
import Password from 'components/form/password';
import { Link } from 'components/configurable-routing';
import config from '../../util/load-config';

const focusOnErrors = createDecorator();

interface LoginProps {
  createBtnOnClick?: () => void;
  forgotPasswordOnClick?: () => void;
  showCloseBtn?: boolean;
  headerText: string;
  showCreateAccountBtn?: boolean;
  guestCheckoutOnClick?: () => void;
  showFirstTimeCustomersMessage?: boolean; // TODO: not being used, but might come back after MAG-308
}

const Login = ({
  createBtnOnClick,
  forgotPasswordOnClick,
  showCloseBtn,
  headerText,
  showCreateAccountBtn,
  guestCheckoutOnClick,
}: LoginProps) => {
  const dispatch = useDispatch();
  const loginMessage = useSelector(
    (state: RootState) => state.global.loginMessage
  );

  const [login, { loading, error, completed }] = useLogin();
  const close = () => dispatch(closeDrawer());

  useEffect(() => {
    if (completed) {
      dispatch(closeDrawer());
    }
  }, [dispatch, completed]);

  return (
    <>
      <SectionTitle
        fontSize={[3, 4]}
        title={headerText}
        closeButtonCallback={showCloseBtn ? close : undefined}
      />

      {loginMessage && <ErrorBox errors={[loginMessage]} />}
      {error && error.graphQLErrors.length > 0 && (
        <ErrorBox errors={error.graphQLErrors.map(e => e.message)} />
      )}

      <Form
        decorators={[focusOnErrors]}
        onSubmit={({ email, password }) => login({ email, password })}
      >
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid mt="4">
              <Field
                name="email"
                validate={value => {
                  if (!value) {
                    return 'Enter your email';
                  }
                  if (!isEmail(value)) {
                    return 'Enter a valid email';
                  }
                }}
              >
                {({ input, meta }) => (
                  <Box>
                    <LabeledInput
                      {...input}
                      label="Email Address"
                      type="email"
                      error={meta.touched && meta.error}
                      invalid={meta.error && meta.touched}
                      placeholder="Email"
                    />
                  </Box>
                )}
              </Field>

              <Field
                name="password"
                validate={value => {
                  if (!value) {
                    return 'Enter your password';
                  }
                }}
              >
                {({ input, meta }) => (
                  <Box>
                    <Password
                      label="Password"
                      placeholder="Password"
                      {...input}
                      invalid={meta.error && meta.touched}
                      error={meta.touched && meta.error}
                    />
                  </Box>
                )}
              </Field>

              <Flex justifyContent="flex-end">
                <Button
                  p="0"
                  fontSize="2"
                  variant="flat"
                  display="inline-block"
                  onClick={() => {
                    forgotPasswordOnClick
                      ? forgotPasswordOnClick()
                      : dispatch(openDrawer('forgot'));
                  }}
                >
                  Forgot password?
                </Button>
              </Flex>

              <Button
                width="100%"
                mb="2"
                py="0.8rem"
                loading={loading}
                disabled={loading}
                type="submit"
                css={theme => css`
                  box-shadow: ${theme.shadows.authButton};
                `}
              >
                Log In
              </Button>

              <Text fontWeight="bold">Continue with</Text>

              <SocialAuth context={AuthenticationContext.LogIn} />

              {showCreateAccountBtn && (
                <>
                  <Box>
                    <Divider text="Or" mb="3" />
                  </Box>

                  <Button
                    p="0"
                    variant="flat"
                    onClick={() =>
                      createBtnOnClick
                        ? createBtnOnClick()
                        : dispatch(openDrawer('createAccount'))
                    }
                  >
                    Create Account
                  </Button>
                </>
              )}
              {guestCheckoutOnClick && (
                <>
                  <Box mt={3}>
                    <Divider text="Or" />
                  </Box>
                  <Flex width="100%" justifyContent="center">
                    <Button variant="flat" p={2} onClick={guestCheckoutOnClick}>
                      Checkout as guest
                    </Button>
                  </Flex>
                  <Text textAlign="center" fontSize={2} lineHeight={1.2}>
                    By using guest checkout you will not be able to have a
                    history of your purchases. Check our full{' '}
                    <Link
                      href={config.helpCentreLinks.guestCheckout}
                      color="primary"
                      fontWeight="bold"
                      target="_blank"
                    >
                      terms and conditions
                    </Link>
                    .
                  </Text>
                </>
              )}
            </Grid>
          </form>
        )}
      </Form>
    </>
  );
};

const Wrapper = (props: LoginProps) => {
  const vodapayIsActive = useVodapayActive();
  if (vodapayIsActive) {
    return <VodapayAuth />;
  }

  return <Login {...props} />;
};

export default Wrapper;
